import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { formatPrice } from "../lib/api";

const IMG = "https://aleo.ro/media/catalog/product/cache/e12079e20fd1e2b418c0a812acc403bd/";

// positions are percentages inside the showcase; callouts sit close to the product, never symmetric
export const HERO_ITEMS = [
  { slug: "carucior-thule-urban-glide-4-all-terrain-black", name: "Thule Urban Glide 4", label: "Nou · all-terrain", price: 3941, image: IMG + "t/a/ta10101976_1.jpg",
    name_: { left: "9%", top: "20%" }, price_: { right: "13%", bottom: "27%" } },
  { slug: "set-carucior-thule-sleek-2-landou-black", name: "Set Thule Sleek 2 + Landou", label: "Set complet · urban", price: 5366, image: IMG + "f/9/f98bc181915ecbb86cdac8d0a9b6610475c3639410accf0583793ec93ecb4d81.jpeg",
    name_: { left: "8%", bottom: "11%" }, price_: { right: "8%", top: "13%" } },
  { slug: "carucior-multisport-double-thule-chariot-sport-2-natural-gold", name: "Thule Chariot Sport 2", label: "Multisport · 2 copii", price: 7403, image: IMG + "b/c/bc2b393da1679f575b8870b60bcbc9818bc94a66875ff18952ef366ae6bf81cb.jpeg",
    name_: { left: "9%", top: "15%" }, price_: { right: "8%", bottom: "36%" } },
  { slug: "set-carucior-thule-charm-2-in-1-sport-landou-darkest-blue", name: "Set Thule Charm 2 în 1", label: "Set 2 în 1 · de la naștere", price: 5802, image: IMG + "4/7/473b27afb11770459b6d59bf50331da4eb2e39c3a42d3fec202f9e5be28168b8.jpeg",
    name_: { right: "7%", bottom: "11%", align: "right" }, price_: { left: "9%", top: "14%" } },
];

const pos = ({ align, ...p }) => p;
const spring = { type: "spring", stiffness: 220, damping: 26 };

export const HeroShowcase = () => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const item = HERO_ITEMS[i];

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % HERO_ITEMS.length), 5200);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <div data-testid="hero-showcase" className="relative aspect-[4/3] lg:aspect-[5/4] bg-white" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <Link to={`/produs/${item.slug}`} data-testid="hero-showcase-link" className="absolute inset-0 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={item.slug}
            className="relative w-[64%] h-[46%] sm:h-[64%] -mt-[10%] sm:mt-0 flex items-center justify-center"
            initial={{ opacity: 0, x: 60, scale: 0.94 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -60, scale: 0.96 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              className="w-full h-full flex items-center justify-center"
              animate={{ y: [0, -14, 0], rotate: [0, 0.6, 0] }}
              transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut" }}
              whileHover={{ scale: 1.03 }}
            >
              <img src={item.image} alt={item.name} className="product-img max-h-full max-w-full object-contain" />
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </Link>

      <AnimatePresence mode="popLayout">
        <motion.div
          key={`name-${item.slug}`}
          data-testid="hero-showcase-name-block"
          className={`absolute pointer-events-none max-w-[48%] bg-white/85 backdrop-blur-sm px-3 py-2 -mx-3 ${item.name_.align === "right" ? "text-right" : ""}`}
          style={pos(item.name_)}
          initial={{ opacity: 0, y: 14, filter: "blur(4px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -14, filter: "blur(4px)" }}
          transition={spring}
        >
          <span className={`block h-px w-10 bg-[#B4A38E] mb-3 ${item.name_.align === "right" ? "ml-auto" : ""}`} />
          <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#9A9A9A]">{item.label}</p>
          <p data-testid="hero-showcase-name" className="font-serif text-lg sm:text-xl lg:text-2xl mt-1 leading-tight">{item.name}</p>
        </motion.div>
      </AnimatePresence>

      <AnimatePresence mode="popLayout">
        <motion.div
          key={`price-${item.slug}`}
          data-testid="hero-showcase-price"
          className="absolute pointer-events-none"
          style={pos(item.price_)}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.85 }}
          transition={{ ...spring, delay: 0.1 }}
        >
          <span className="inline-flex items-center gap-2 border border-black/10 bg-white/80 backdrop-blur px-3 py-1.5 text-xs font-medium tracking-wide">
            <span className="h-1.5 w-1.5 rounded-full bg-[#0A0A0A]" />
            {formatPrice(item.price)}
          </span>
        </motion.div>
      </AnimatePresence>

      <div className="absolute left-1/2 -translate-x-1/2 bottom-1 flex items-center gap-4">
        <div className="flex gap-1.5">
          {HERO_ITEMS.map((h, k) => (
            <button key={h.slug} data-testid={`hero-dot-${k}`} aria-label={h.name} onClick={() => setI(k)} className={`h-[2px] transition-[width,background-color] duration-300 ${k === i ? "w-8 bg-[#0A0A0A]" : "w-3 bg-black/20 hover:bg-black/50"}`} />
          ))}
        </div>
        <Link to={`/produs/${item.slug}`} aria-label="Vezi produsul" className="hidden sm:flex h-8 w-8 text-[#0A0A0A] items-center justify-center hover:rotate-45 transition-transform duration-300"><ArrowUpRight size={15} /></Link>
      </div>
    </div>
  );
};
